import { Close, Settings, SmartToy } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Chip,
    IconButton,
    Slider,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext';
import {
    ENIQ_INCLUDED_IN_TIER,
    ENIQ_MIN_BILLABLE_USERS,
    ENIQ_TIER_PRO,
    isEniqIncludedInTier,
    normalizeEniqTier,
} from '../../../../utils/eniqPricing';
import { getEffectiveTierUsers } from '../../../../utils/tierUsers';
import EniqAISettingsDialog from './EniqAISettingsDialog';
import EniqCompareDialog from './EniqCompareDialog';
import EniqTierSelector from './EniqTierSelector';

const EniqAITierSelect = () => {
  const { t } = useTranslation();
  const { region } = useUserPreferences();
  const {
    eniqTier,
    setEniqTier,
    eniqStandaloneUsers,
    setEniqStandaloneUsers,
    eniqAiSettingsOpen,
    hasEniqAiCustomSettings,
    setEniqAiSettingsOpen,
    packageTier,
    businessPhoneUsers,
    fmcUsers,
    businessPhoneType,
    isProductSelected,
    toggleProduct,
    veryCustomProductDiscounts,
  } = useCalculator();

  const tier = normalizeEniqTier(eniqTier);
  const isIncludedInTier = isEniqIncludedInTier(tier, packageTier);

  // Eniq users follow Business Phone / FMC. Without them Eniq is sold
  // standalone with its own editable user count. In every mode Eniq bills
  // at least ENIQ_MIN_BILLABLE_USERS (5), so the card shows the billed count.
  const hasUserBase = isProductSelected('business-phone') || isProductSelected('fmc');
  const linkedUsers = getEffectiveTierUsers({ businessPhoneUsers, fmcUsers, businessPhoneType, packageTier });
  const billedLinkedUsers = Math.max(linkedUsers, ENIQ_MIN_BILLABLE_USERS);

  const [compareOpen, setCompareOpen] = useState(false);
  // The billed standalone user count lives in CalculatorContext (persisted per
  // tab and sent as the bundle quantity); only the raw input text stays local.
  const [standaloneInput, setStandaloneInput] = useState(String(eniqStandaloneUsers));

  useEffect(() => {
    setStandaloneInput(String(eniqStandaloneUsers));
  }, [eniqStandaloneUsers]);

  const effectiveUsers = hasUserBase ? billedLinkedUsers : eniqStandaloneUsers;

  const hasAnyCustomSettings =
    hasEniqAiCustomSettings ||
    (veryCustomProductDiscounts['eniq_ai'] && veryCustomProductDiscounts['eniq_ai'] > 0);

  const handleStandaloneBlur = () => {
    const parsed = Number(standaloneInput);
    const corrected = Number.isFinite(parsed) && parsed > ENIQ_MIN_BILLABLE_USERS
      ? Math.floor(parsed)
      : ENIQ_MIN_BILLABLE_USERS;
    setEniqStandaloneUsers(corrected);
    setStandaloneInput(String(corrected));
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SmartToy sx={{ color: '#333', fontSize: 20, mr: 1 }} />
            <Typography sx={{ fontWeight: 500 }}>
              {t('calculator.eniqAiLabel', 'Eniq AI')}
            </Typography>
            {isIncludedInTier && (
              <Chip
                label={t('calculator.included', 'INCLUDED')}
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: 'rgba(0, 200, 83, 0.1)',
                  color: '#00C853',
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  height: 20,
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={t('calculator.eniqAiSettings', 'Eniq AI Settings')}>
              <IconButton
                size="small"
                onClick={() => setEniqAiSettingsOpen(true)}
                sx={{
                  color: hasAnyCustomSettings ? '#00C853' : '#757575',
                  mr: 1,
                  '&:hover': { color: '#00C853' }
                }}
              >
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Included in the tier: the product cannot be removed */}
            {!isIncludedInTier && (
              <IconButton
                size="small"
                onClick={() => toggleProduct('eniq-ai')}
                sx={{ color: '#333', '&:hover': { color: '#00C853' } }}
              >
                <Close fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Eniq tier: Standard / Pro */}
        <Box sx={{ mt: 2 }}>
          <EniqTierSelector
            selected={tier}
            onChange={setEniqTier}
            region={region}
            packageTier={packageTier}
            hasPhoneAnchor={hasUserBase}
            onCompareClick={() => setCompareOpen(true)}
          />
        </Box>

        {packageTier === ENIQ_INCLUDED_IN_TIER && tier === ENIQ_TIER_PRO && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            {t('calculator.eniqUpgradeDiff', 'Standard is included in Corporate — you only pay the difference')}
          </Typography>
        )}

        {/* Users */}
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, mb: 0.5 }}>
          {t('calculator.eniqUsersLabel', 'Number of users')}
        </Typography>

        {hasUserBase ? (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Slider
                  value={effectiveUsers}
                  min={1}
                  max={Math.max(100, effectiveUsers)}
                  disabled
                  sx={{
                    color: '#00C853',
                    '& .MuiSlider-thumb': { width: 16, height: 16, backgroundColor: '#00C853' }
                  }}
                />
              </Box>
              <TextField
                value={effectiveUsers}
                disabled
                type="number"
                variant="outlined"
                size="small"
                sx={{ width: 90 }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5 }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('calculator.eniqUsersLinkedInfo', 'Eniq AI users are linked to Business Phone/FMC users count')}
              </Typography>
            </Box>
            {linkedUsers < ENIQ_MIN_BILLABLE_USERS && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                {t('calculator.eniqMinBillableUsers', 'Eniq AI is always billed for a minimum of {{count}} users', { count: ENIQ_MIN_BILLABLE_USERS })}
              </Typography>
            )}
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ flex: 1, mr: 2 }}>
                <Slider
                  value={Math.max(eniqStandaloneUsers, ENIQ_MIN_BILLABLE_USERS)}
                  min={ENIQ_MIN_BILLABLE_USERS}
                  max={100}
                  onChange={(_, v) => setEniqStandaloneUsers(Number(v))}
                  sx={{
                    color: '#00C853',
                    '& .MuiSlider-thumb': { width: 16, height: 16, backgroundColor: '#00C853' }
                  }}
                />
              </Box>
              <TextField
                value={standaloneInput}
                onChange={(e) => setStandaloneInput(e.target.value)}
                onBlur={handleStandaloneBlur}
                type="number"
                variant="outlined"
                size="small"
                sx={{ width: 90 }}
                inputProps={{ inputMode: 'numeric' }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
              {t('calculator.eniqMinBillableUsers', 'Eniq AI is always billed for a minimum of {{count}} users', { count: ENIQ_MIN_BILLABLE_USERS })}
            </Typography>
          </>
        )}

        {isIncludedInTier && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {t('calculator.includedInTier', 'Included in your {{tier}} plan', {
              tier: packageTier.charAt(0).toUpperCase() + packageTier.slice(1)
            })}
          </Typography>
        )}
      </Box>

      {/* Settings Dialog */}
      {eniqAiSettingsOpen !== undefined && <EniqAISettingsDialog />}

      {/* Standard vs Pro comparison */}
      <EniqCompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        region={region}
        packageTier={packageTier}
        hasPhoneAnchor={hasUserBase}
        selectedTier={tier}
        onTierSelect={(next) => {
          setEniqTier(next);
          setCompareOpen(false);
        }}
      />
    </>
  );
};

export default EniqAITierSelect;
